"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const classnames_1 = __importDefault(require("classnames"));
const react_transition_group_1 = require("react-transition-group");
const useEventListener_1 = __importDefault(require("../../hooks/useEventListener"));
const useAfterFirstRender_1 = __importDefault(require("../../hooks/useAfterFirstRender"));
const function_utils_1 = require("../../utils/function-utils");
const backwardCompatibilityForProperties_1 = require("../../helpers/backwardCompatibilityForProperties");
const CounterConstants_1 = require("./CounterConstants");
require("./Counter.scss");
const Counter = ({ className, 
// Backward compatibility for props naming
wrapperClassName, count, size, kind, color, maxDigits, ariaLabeledBy, ariaLabel, id, prefix, onMouseDown, noAnimation }) => {
    // Variables
    const overrideClassName = (0, backwardCompatibilityForProperties_1.backwardCompatibilityForProperties)([className, wrapperClassName], undefined);
    // State
    const [countChangeAnimationState, setCountChangeAnimationState] = (0, react_1.useState)(false);
    // Refs
    const ref = (0, react_1.useRef)(null);
    // Callbacks
    const setCountChangedAnimationActive = (0, react_1.useCallback)(() => {
        setCountChangeAnimationState(true);
    }, [setCountChangeAnimationState]);
    const setCountChangedAnimationNotActive = (0, react_1.useCallback)(() => {
        setCountChangeAnimationState(false);
    }, [setCountChangeAnimationState]);
    // Listeners
    (0, useEventListener_1.default)({
        eventName: "animationend",
        callback: setCountChangedAnimationNotActive,
        ref
    });
    // Custom hooks
    const isAfterFirstRender = (0, useAfterFirstRender_1.default)();
    // Effects
    (0, react_1.useEffect)(() => {
        if (!isAfterFirstRender.current) {
            setCountChangedAnimationActive();
        }
    }, [count, isAfterFirstRender, setCountChangedAnimationActive]);
    (0, react_1.useEffect)(() => {
        if (maxDigits <= 0) {
            console.error("Max digits must be a positive number");
        }
    }, [maxDigits]);
    // Memos
    const classNames = (0, react_1.useMemo)(() => {
        return (0, classnames_1.default)("monday-style-counter", `monday-style-counter--size-${(0, CounterConstants_1.getActualSize)(size)}`, `monday-style-counter--kind-${kind}`, `monday-style-counter--color-${color}`, {
            "monday-style-counter--with-animation": countChangeAnimationState
        });
    }, [size, kind, color, countChangeAnimationState]);
    const countText = (count === null || count === void 0 ? void 0 : count.toString().length) > maxDigits ? `${Math.pow(10, maxDigits) - 1}+` : String(count);
    const counter = (0, jsx_runtime_1.jsx)("span", Object.assign({ id: `counter-${id}` }, { children: prefix + countText }));
    return ((0, jsx_runtime_1.jsx)("span", Object.assign({ className: overrideClassName, "aria-label": `${ariaLabel} ${countText}`, "aria-labelledby": ariaLabeledBy, onMouseDown: onMouseDown }, { children: (0, jsx_runtime_1.jsx)("div", Object.assign({ className: classNames, "aria-label": countText, ref: ref }, { children: noAnimation ? (counter) : ((0, jsx_runtime_1.jsx)(react_transition_group_1.SwitchTransition, Object.assign({ mode: "out-in" }, { children: (0, jsx_runtime_1.jsx)(react_transition_group_1.CSSTransition, Object.assign({ classNames: "monday-style-counter--fade", 
                    // @ts-expect-error @definitelyTyped typings expecting a single parameter for some reason when the function passed here is called with two parameters
                    // See https://github.com/reactjs/react-transition-group/blob/c89f807067b32eea6f68fd6c622190d88ced82e2/src/Transition.js#L522-L534
                    addEndListener: (node, done) => {
                        node.addEventListener("transitionend", done, false);
                    } }, { children: (0, jsx_runtime_1.jsx)("span", Object.assign({ id: `counter-${id}` }, { children: prefix + countText })) }), countText) }))) })) })));
};
Object.assign(Counter, {
    sizes: CounterConstants_1.CounterSize,
    colors: CounterConstants_1.CounterColor,
    kinds: CounterConstants_1.CounterType
});
Counter.defaultProps = {
    ariaLabel: "",
    ariaLabeledBy: "",
    className: undefined,
    color: CounterConstants_1.CounterColor.PRIMARY,
    count: 0,
    id: "",
    kind: CounterConstants_1.CounterType.FILL,
    maxDigits: 3,
    noAnimation: false,
    onMouseDown: function_utils_1.NOOP,
    prefix: "",
    size: CounterConstants_1.CounterSize.LARGE
};
exports.default = Counter;
